import { AlertBar, Button, Typography } from '@packages/ui/shared';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

/** Style for the alert bar inside the submit confirmation modal. */
const alertBarStyle = { marginBottom: 24 };

/** Style for the cancel button's right margin. */
const cancelButtonStyle = { marginRight: 2 };

/**
 * Props for the SubmitConfirmationModal component.
 *
 * Controls visibility, loading state, and user actions for the
 * submit case confirmation modal.
 */
export type SubmitConfirmationModalProps = {
    /** The alert bar message to display inside the modal (empty string hides it). */
    alertBarMessage: string;

    /** Whether the case is currently being submitted (disables buttons, shows spinner). */
    isSubmitting: boolean;

    /** Whether this modal is currently visible. */
    isVisible: boolean;

    /** Called when the user dismisses the modal alert bar. */
    onAlertBarClose: () => void;

    /** Called when the user clicks Cancel to close the modal without action. */
    onCancel: () => void;

    /** Called when the user confirms submission. */
    onSubmit: () => void;
};

/**
 * Submit case confirmation modal for the CID-CVN worksheet subview.
 * Asks the user to confirm before submitting the case for review.
 *
 * @param props - component props
 * @returns the submit case confirmation modal
 */
export function SubmitConfirmationModal({
    alertBarMessage,
    isSubmitting,
    isVisible,
    onAlertBarClose,
    onCancel,
    onSubmit,
}: SubmitConfirmationModalProps) {
    // Hooks
    const { t } = useTranslation();

    return (
        <Modal centered onHide={onCancel} show={isVisible}>
            <Modal.Header>
                <Modal.Title>{t('techline:submitCaseModal.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <AlertBar error={true} onClose={onAlertBarClose} show={Boolean(alertBarMessage)} style={alertBarStyle}>
                    <Typography>{alertBarMessage}</Typography>
                </AlertBar>
                <Typography>{t('techline:submitCaseModal.message')}</Typography>
            </Modal.Body>
            <Modal.Footer>
                <Button disabled={isSubmitting} onPress={onCancel} style={cancelButtonStyle} variant="ghost-blue">
                    {t('buttons:cancel')}
                </Button>
                <Button disabled={isSubmitting} isLoading={isSubmitting} onPress={onSubmit} variant="primary">
                    {t('buttons:submit')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
